import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { ClinicalFilters } from "./components/ClinicalSettings/ClinicalSettings";
import { ChatAppResponse, PatientBriefResponse, PatientTimelineResponse, PatientDemographics, PatientCaseReview } from "./api/models";
import { SHOW_SUMMARIES } from "./featureFlags";
import {
    SelectedNoteScope,
    SelectedNoteScopeSourceFilters,
    SelectedNoteScopeStatus,
    SelectedNoteScopeMode,
    defaultSelectedNoteScope,
    clearSelectedNoteScope as makeClearedSelectedNoteScope,
    revalidateSelectedNoteScope,
    selectedNoteScopeSignature,
    selectedNoteScopeSourceFilters
} from "./selectedNoteScope";

// --- Caseload team/CM selection (nosum app) ---
export interface SelectedPatient {
    consumerId: string;
    consumer: string;
}

export interface CaseloadSelection {
    supervisor: string;
    team: string;
    division: string;
    caseManager: string;
    selectedPatients: SelectedPatient[];
    teamPatients: SelectedPatient[];  // Full patient list for current team/CM (Brief picker "all" pool)
}

const defaultCaseloadSelection: CaseloadSelection = { supervisor: "", team: "", division: "", caseManager: "", selectedPatients: [], teamPatients: [] };
const SELECTED_NOTE_SCOPE_STORAGE_KEY = "selectedNoteScope";
const BRIEF_PATIENT_STORAGE_KEY = "briefPatient";
export type { SelectedNoteScope, SelectedNoteScopeSourceFilters, SelectedNoteScopeStatus, SelectedNoteScopeMode };

function loadCaseloadFromSession(): CaseloadSelection {
    try {
        const raw = sessionStorage.getItem("caseloadSelection");
        return raw ? { ...defaultCaseloadSelection, ...JSON.parse(raw) } : defaultCaseloadSelection;
    } catch {
        return defaultCaseloadSelection;
    }
}

function loadSelectedNoteScopeFromSession(): SelectedNoteScope {
    try {
        const raw = sessionStorage.getItem(SELECTED_NOTE_SCOPE_STORAGE_KEY);
        return raw ? { ...defaultSelectedNoteScope, ...JSON.parse(raw), source: "browse" } : defaultSelectedNoteScope;
    } catch {
        return defaultSelectedNoteScope;
    }
}

function loadBriefPatientFromSession(): { patId: string; patientName: string } {
    try {
        const raw = sessionStorage.getItem(BRIEF_PATIENT_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return {
            patId: typeof parsed.patId === "string" ? parsed.patId : "",
            patientName: typeof parsed.patientName === "string" ? parsed.patientName : ""
        };
    } catch {
        return { patId: "", patientName: "" };
    }
}

export const defaultClinicalFilters: ClinicalFilters = {
    patId: "",
    patientName: "",
    noteTypes: [],
    formNames: [],
    serviceCodes: [],
    clinicalActivities: [],
    assessmentScales: [],
    servicePrograms: [],
    serviceDescriptions: [],
    providers: [],
    caseManagers: [],
    assessmentStaff: [],
    dateRanges: [],
    consumers: [],
    severityIndicators: [],
    topResults: 0, // 0 = All
    sortOrder: "newest"
};

// Add FilterOptions type (or import from ClinicalSettings)
import { IDropdownOption } from "@fluentui/react";

interface FilterOptions {
    noteTypes: IDropdownOption[];
    formNames: IDropdownOption[];
    serviceCodes: IDropdownOption[];
    clinicalActivities: IDropdownOption[];
    assessmentScales: IDropdownOption[];
    servicePrograms: IDropdownOption[];
    serviceDescriptions: IDropdownOption[];
    providers: IDropdownOption[];
    caseManagers: IDropdownOption[];
    assessmentStaff: IDropdownOption[];
    dateRanges: IDropdownOption[];
    consumers: IDropdownOption[];
    severityIndicators: IDropdownOption[];
}

interface ClinicalContextType {
    filters: ClinicalFilters;
    setFilters: (filters: ClinicalFilters | ((prev: ClinicalFilters) => ClinicalFilters)) => void;
    // Caseload selection (nosum)
    caseloadSelection: CaseloadSelection;
    setCaseloadSelection: (sel: CaseloadSelection | ((prev: CaseloadSelection) => CaseloadSelection)) => void;
    filterOptions: FilterOptions | null;
    setFilterOptions: (options: FilterOptions | null) => void;
    initialFilterOptions: FilterOptions | null;
    setInitialFilterOptions: (options: FilterOptions | null) => void;
    patientInfo: any;
    setPatientInfo: (info: any) => void;
    facetCounts: any;
    setFacetCounts: (counts: any) => void;
    totalNotesAvailable: number;
    setTotalNotesAvailable: (count: number) => void;
    lastUrlPatId: string | null;
    setLastUrlPatId: (patId: string | null) => void;
    loadedPatId: string | null;
    setLoadedPatId: (patId: string | null) => void;
    filteredNotesCount: number;
    setFilteredNotesCount: (count: number) => void;
    selectedNoteScope: SelectedNoteScope;
    setSelectedNoteScope: (scope: SelectedNoteScope | ((prev: SelectedNoteScope) => SelectedNoteScope)) => void;
    clearSelectedNoteScope: () => void;
    // Add these new state types and fields to the context:

    // Add to interface ClinicalContextType (after line 54):
    // Chat page state
    chatAnswers: [string, ChatAppResponse][];
    setChatAnswers: (answers: [string, ChatAppResponse][]) => void;
    chatLastQuestion: string;
    setChatLastQuestion: (question: string) => void;

    // Ask page state
    askAnswer: ChatAppResponse | undefined;
    setAskAnswer: (answer: ChatAppResponse | undefined) => void;
    askLastQuestion: string;
    setAskLastQuestion: (question: string) => void;
    // POC consumers (static, loaded once)
    pocConsumers: IDropdownOption[];
    setPocConsumers: (options: IDropdownOption[]) => void;
    // Brief page — independent patient selection (does not affect Chat/Browse)
    briefPatId: string;
    briefPatientName: string;
    setBriefPatient: (patId: string, patientName: string) => void;
    briefLoadedPatId: string | null;
    setBriefLoadedPatId: (patId: string | null) => void;
    // Brief page cached data
    briefData: PatientBriefResponse | null;
    setBriefData: (data: PatientBriefResponse | null) => void;
    briefTimeline: PatientTimelineResponse | null;
    setBriefTimeline: (data: PatientTimelineResponse | null) => void;
    briefDemographics: PatientDemographics | null;
    setBriefDemographics: (data: PatientDemographics | null) => void;
    briefCaseReview: PatientCaseReview | null;
    setBriefCaseReview: (data: PatientCaseReview | null) => void;
    briefNoteSources: IDropdownOption[];
    setBriefNoteSources: (options: IDropdownOption[]) => void;
    briefSelectedNoteType: string;
    setBriefSelectedNoteType: (noteType: string) => void;
}

export const ClinicalContext = createContext<ClinicalContextType>({
    filters: defaultClinicalFilters,
    setFilters: () => {},
    caseloadSelection: defaultCaseloadSelection,
    setCaseloadSelection: () => {},
    filterOptions: null,
    setFilterOptions: () => {},
    initialFilterOptions: null,
    setInitialFilterOptions: () => {},
    patientInfo: null,
    setPatientInfo: () => {},
    facetCounts: null,
    setFacetCounts: () => {},
    totalNotesAvailable: 0,
    setTotalNotesAvailable: () => {},
    lastUrlPatId: null,
    setLastUrlPatId: () => {},
    loadedPatId: null,
    setLoadedPatId: () => {},
    filteredNotesCount: 0,
    setFilteredNotesCount: () => {},
    selectedNoteScope: defaultSelectedNoteScope,
    setSelectedNoteScope: () => {},
    clearSelectedNoteScope: () => {},
    askAnswer: undefined,
    setAskAnswer: () => {},
    askLastQuestion: "",
    setAskLastQuestion: () => {},
    chatAnswers: [],
    setChatAnswers: () => {},
    chatLastQuestion: "",
    setChatLastQuestion: () => {},
    pocConsumers: [],
    setPocConsumers: () => {},
    briefPatId: "",
    briefPatientName: "",
    setBriefPatient: () => {},
    briefLoadedPatId: null,
    setBriefLoadedPatId: () => {},
    briefData: null,
    setBriefData: () => {},
    briefTimeline: null,
    setBriefTimeline: () => {},
    briefDemographics: null,
    setBriefDemographics: () => {},
    briefCaseReview: null,
    setBriefCaseReview: () => {},
    briefNoteSources: [],
    setBriefNoteSources: () => {},
    briefSelectedNoteType: "Case Review",
    setBriefSelectedNoteType: () => {}
});

export const useClinicalFilters = () => useContext(ClinicalContext);

interface ClinicalProviderProps {
    children: ReactNode;
}

export const ClinicalProvider = ({ children }: ClinicalProviderProps) => {
    const [filters, setFiltersState] = useState<ClinicalFilters>(defaultClinicalFilters);
    // Caseload selection
    const [caseloadSelection, setCaseloadSelectionState] = useState<CaseloadSelection>(loadCaseloadFromSession);

    // Persist caseload selection to sessionStorage
    useEffect(() => {
        sessionStorage.setItem("caseloadSelection", JSON.stringify(caseloadSelection));
    }, [caseloadSelection]);

    const setCaseloadSelection = (sel: CaseloadSelection | ((prev: CaseloadSelection) => CaseloadSelection)) => {
        setCaseloadSelectionState(prev => typeof sel === "function" ? sel(prev) : sel);
    };

    const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
    const [initialFilterOptions, setInitialFilterOptions] = useState<FilterOptions | null>(null);
    const [patientInfo, setPatientInfo] = useState<any>(null);
    const [facetCounts, setFacetCounts] = useState<any>(null);
    const [totalNotesAvailable, setTotalNotesAvailable] = useState<number>(0);
    const [lastUrlPatId, setLastUrlPatId] = useState<string | null>(null);
    const [loadedPatId, setLoadedPatId] = useState<string | null>(null);
    const [filteredNotesCount, setFilteredNotesCount] = useState<number>(0);
    const [selectedNoteScope, setSelectedNoteScopeState] = useState<SelectedNoteScope>(loadSelectedNoteScopeFromSession);
    // Chat page state
    const [chatAnswers, setChatAnswers] = useState<[string, ChatAppResponse][]>([]);
    const [chatLastQuestion, setChatLastQuestion] = useState<string>("");

    // Ask page state
    const [askAnswer, setAskAnswer] = useState<ChatAppResponse | undefined>(undefined);
    const [askLastQuestion, setAskLastQuestion] = useState<string>("");

    // POC consumers (static, loaded once)
    const [pocConsumers, setPocConsumers] = useState<IDropdownOption[]>([]);
    // Brief page — independent patient selection
    const initialBriefPatient = loadBriefPatientFromSession();
    const [briefPatId, setBriefPatIdState] = useState<string>(initialBriefPatient.patId);
    const [briefPatientName, setBriefPatientNameState] = useState<string>(initialBriefPatient.patientName);
    const setBriefPatient = (patId: string, patientName: string) => {
        setBriefPatIdState(patId);
        setBriefPatientNameState(patientName);
        // Clear Brief caches when Brief patient changes
        setBriefLoadedPatId(null);
        setBriefData(null);
        setBriefTimeline(null);
        setBriefDemographics(null);
        setBriefCaseReview(null);
        setBriefNoteSources([]);
        setBriefSelectedNoteType("Case Review");
    };
    const [briefLoadedPatId, setBriefLoadedPatId] = useState<string | null>(null);
    // Brief page cached data
    const [briefData, setBriefData] = useState<PatientBriefResponse | null>(null);
    const [briefTimeline, setBriefTimeline] = useState<PatientTimelineResponse | null>(null);
    const [briefDemographics, setBriefDemographics] = useState<PatientDemographics | null>(null);
    const [briefCaseReview, setBriefCaseReview] = useState<PatientCaseReview | null>(null);
    const [briefNoteSources, setBriefNoteSources] = useState<IDropdownOption[]>([]);
    const [briefSelectedNoteType, setBriefSelectedNoteType] = useState<string>("Case Review");

    useEffect(() => {
        if (!briefPatId) {
            sessionStorage.removeItem(BRIEF_PATIENT_STORAGE_KEY);
            return;
        }
        sessionStorage.setItem(BRIEF_PATIENT_STORAGE_KEY, JSON.stringify({ patId: briefPatId, patientName: briefPatientName }));
    }, [briefPatId, briefPatientName]);

    const setFilters = (newFilters: ClinicalFilters | ((prev: ClinicalFilters) => ClinicalFilters)) => {
        setFiltersState(prev => {
            const updated = typeof newFilters === "function" ? newFilters(prev) : newFilters;
            // If patient changed, reset other filters AND cached data
            if (updated.patId !== prev.patId && updated.patId !== "") {
                setFilterOptions(null);
                setInitialFilterOptions(null);
                setPatientInfo(null);
                setFacetCounts(null);
                setTotalNotesAvailable(0);
                setLoadedPatId(null);
                setChatAnswers([]);
                setChatLastQuestion("");
                setAskAnswer(undefined);
                setAskLastQuestion("");
                setSelectedNoteScopeState(defaultSelectedNoteScope);
                // Note: Brief caches NOT cleared here — Brief uses briefPatId independently
                return { ...defaultClinicalFilters, patId: updated.patId, patientName: updated.patientName };
            }
            return updated;
        });
    };

    const clearScopeDependentUiState = () => {
        setChatAnswers([]);
        setChatLastQuestion("");
        setAskAnswer(undefined);
        setAskLastQuestion("");
    };

    const setSelectedNoteScope = (scope: SelectedNoteScope | ((prev: SelectedNoteScope) => SelectedNoteScope)) => {
        setSelectedNoteScopeState(prev => {
            const next = typeof scope === "function" ? scope(prev) : scope;
            const normalized: SelectedNoteScope = { ...defaultSelectedNoteScope, ...next, source: "browse" };
            if (selectedNoteScopeSignature(prev) !== selectedNoteScopeSignature(normalized)) {
                clearScopeDependentUiState();
            }
            return normalized;
        });
    };

    const clearSelectedNoteScope = () => {
        setSelectedNoteScope(makeClearedSelectedNoteScope(new Date().toISOString()));
    };

    useEffect(() => {
        sessionStorage.setItem(SELECTED_NOTE_SCOPE_STORAGE_KEY, JSON.stringify(selectedNoteScope));
    }, [selectedNoteScope]);

    useEffect(() => {
        if (selectedNoteScope.status !== "active" && selectedNoteScope.status !== "broadened") return;
        const currentPatientIds = filters.patId
            ? [filters.patId]
            : !SHOW_SUMMARIES
              ? caseloadSelection.selectedPatients.map(patient => patient.consumerId)
              : [];
        if (currentPatientIds.length === 0 || selectedNoteScope.patientIds.length === 0) return;
        const revalidated = revalidateSelectedNoteScope(selectedNoteScope, currentPatientIds, selectedNoteScopeSourceFilters(filters), new Date().toISOString());
        if (selectedNoteScopeSignature(revalidated) !== selectedNoteScopeSignature(selectedNoteScope)) {
            setSelectedNoteScopeState(revalidated);
            clearScopeDependentUiState();
        }
    }, [filters, caseloadSelection.selectedPatients, selectedNoteScope]);

    // --- Nosum: bidirectional sync between consumer facet filter and caseload selectedPatients ---
    // filters.consumers now stores ConsumerIDs, so sync is direct ID comparison.
    const prevConsumersRef = useRef<string[]>([]);

    // Consumer filter change → update selectedPatients to match
    useEffect(() => {
        const prevConsumers = prevConsumersRef.current;
        prevConsumersRef.current = filters.consumers;

        if (SHOW_SUMMARIES || caseloadSelection.teamPatients.length === 0) return;

        if (filters.consumers.length > 0) {
            const selectedIdSet = new Set(filters.consumers);
            setCaseloadSelectionState(prev => {
                const matching = prev.teamPatients.find(p => selectedIdSet.has(p.consumerId));
                if (!matching) return prev;
                const currentIds = new Set(prev.selectedPatients.map(p => p.consumerId));
                if (currentIds.size === 1 && currentIds.has(matching.consumerId)) return prev;
                return { ...prev, selectedPatients: [matching] };
            });
        } else if (prevConsumers.length > 0) {
            // Consumer filter just cleared — restore the top patient
            setCaseloadSelectionState(prev => {
                const firstPatient = prev.teamPatients[0];
                if (!firstPatient) return prev;
                if (prev.selectedPatients.length === 1 && prev.selectedPatients[0]?.consumerId === firstPatient.consumerId) return prev;
                return { ...prev, selectedPatients: [firstPatient] };
            });
        }
    }, [filters.consumers]);

    // Caseload deselect → clean stale consumer filter entries
    useEffect(() => {
        if (SHOW_SUMMARIES || filters.consumers.length === 0) return;
        const selectedIds = new Set(caseloadSelection.selectedPatients.map(p => p.consumerId));
        const cleaned = filters.consumers.filter(id => selectedIds.has(id));
        if (cleaned.length !== filters.consumers.length) {
            setFiltersState(prev => ({ ...prev, consumers: cleaned }));
        }
    }, [caseloadSelection.selectedPatients]);

    return (
        <ClinicalContext.Provider
            value={{
                filters,
                setFilters,
                filterOptions,
                setFilterOptions,
                initialFilterOptions,
                setInitialFilterOptions,
                patientInfo,
                setPatientInfo,
                facetCounts,
                setFacetCounts,
                totalNotesAvailable,
                setTotalNotesAvailable,
                lastUrlPatId,
                setLastUrlPatId,
                loadedPatId,
                filteredNotesCount,
                setFilteredNotesCount,
                selectedNoteScope,
                setSelectedNoteScope,
                clearSelectedNoteScope,
                setLoadedPatId,
                chatAnswers,
                setChatAnswers,
                chatLastQuestion,
                setChatLastQuestion,
                askAnswer,
                setAskAnswer,
                askLastQuestion,
                setAskLastQuestion,
                pocConsumers,
                setPocConsumers,
                briefPatId,
                briefPatientName,
                setBriefPatient,
                briefLoadedPatId,
                setBriefLoadedPatId,
                briefData,
                setBriefData,
                briefTimeline,
                setBriefTimeline,
                briefDemographics,
                setBriefDemographics,
                briefCaseReview,
                setBriefCaseReview,
                briefNoteSources,
                setBriefNoteSources,
                briefSelectedNoteType,
                setBriefSelectedNoteType,
                caseloadSelection,
                setCaseloadSelection
            }}
        >
            {children}
        </ClinicalContext.Provider>
    );
};
